import { useFlags } from "@aichat/aichat-lib";
import { languages } from "@aichat/constants/languages";
import { FlagsmithTraits } from "@aichat/types/index";
import {
  createClient,
  type ListenLiveClient,
  LiveTranscriptionEvents,
} from "@deepgram/sdk";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

const REALTIME_MS = 1000;

const deepgramLiveConfig = {
  smart_format: true,
  vad_events: true,
  utterance_end_ms: REALTIME_MS,
  endpointing: 100,
  interim_results: true,
};

interface IUseVoiceParams {
  onChange: (transcript: string) => void;
  onTranscript?: (transcript: string) => void;
  deepgramApiKey?: string;
  isRecording: boolean;
  setIsRecording: (isRecording: boolean) => void;
  language: string | null;
  clientDomain: string;
  isVoiceBotSpeaking: boolean;
}

function transformArray(arr: number[]) {
  let newArr: number[] = [];
  for (let i = 0; i < arr.length; i++) {
    if (i % 2 === 0) newArr.push(arr[i]!);
    else newArr.unshift(arr[i]!);
  }
  return newArr;
}

export async function getOpenaiTranscriptionSession({
  language,
  clientDomain,
}: {
  language: string | null;
  clientDomain: string;
}) {
  const { data } = await axios.post(
    `/widget/openai/transcription_session`,
    {
      language: language?.split("-")[0],
      prompt: `Transcribe the speech in ${languages[language as keyof typeof languages]}.`,
    },
    {
      headers: {
        "x-client-domain": clientDomain,
      },
    },
  );
  return data;
}

const newModelSupportedLanguages = [
  "multi",
  "en",
  "en-US",
  "es",
  "fr",
  "de",
  "hi",
  "ru",
  "pt",
  "ja",
  "it",
  "nl",
];

export default function useVoice({
  onChange,
  deepgramApiKey,
  isRecording,
  setIsRecording,
  onTranscript,
  language,
  isVoiceBotSpeaking,
  clientDomain,
}: IUseVoiceParams) {
  const { enable_openai_transcription: enableOpenaiTranscription } = useFlags(
    [],
    [FlagsmithTraits.ENABLE_OPENAI_TRANSCRIPTION],
  );
  const [isConnected, setIsConnected] = useState(true);
  const transcriptRef = useRef("");
  const mediaRecorderRef = useRef<MediaRecorder>();
  const isSpeechFinal = useRef(false);
  const deepgram = useRef(createClient(deepgramApiKey)).current;
  const trackRef = useRef<MediaStreamTrack | undefined>();
  // New refs and state for frequency data
  const [frequencyData, setFrequencyData] = useState<Uint8Array>(
    new Uint8Array(64),
  );
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const initFrequencyData = useCallback((stream: MediaStream) => {
    audioContextRef.current = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    analyzerRef.current = audioContextRef.current.createAnalyser();
    analyzerRef.current.fftSize = 128;

    const source = audioContextRef.current.createMediaStreamSource(stream);
    source.connect(analyzerRef.current);
  }, []);

  const updateFrequencyData = useCallback(() => {
    if (analyzerRef.current) {
      const dataArray = new Uint8Array(analyzerRef.current.frequencyBinCount);
      analyzerRef.current.getByteFrequencyData(dataArray);
      setFrequencyData(new Uint8Array(transformArray(Array.from(dataArray))));
    }
    rafIdRef.current = requestAnimationFrame(updateFrequencyData);
  }, []);

  useEffect(() => {
    if (!(isRecording && language && !enableOpenaiTranscription)) return;

    let stream: MediaStream;
    let connection: ListenLiveClient;
    let mediaRecorder: MediaRecorder;
    // eslint-disable-next-line no-undef
    let interval: NodeJS.Timeout;
    let isMounted = true;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((_stream) => {
        if (!isMounted) return;
        stream = _stream;
        mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.start();

        // Set up AudioContext and AnalyserNode
        initFrequencyData(stream);
        // Start updating frequency data
        updateFrequencyData();

        connection = deepgram.listen.live({
          ...deepgramLiveConfig,
          language: newModelSupportedLanguages.includes(language)
            ? "multi"
            : language,
          model: newModelSupportedLanguages.includes(language)
            ? "nova-3"
            : "nova-2",
        });

        connection.on(LiveTranscriptionEvents.Open, () => {
          connection.on(LiveTranscriptionEvents.Close, () => {
            console.log("Connection closed.");
          });

          connection.on(LiveTranscriptionEvents.Transcript, (data) => {
            if (isSpeechFinal.current) return;
            const transcript = data.channel.alternatives[0].transcript;

            function onFinal() {
              isSpeechFinal.current = true;
              transcriptRef.current = "";
              setIsRecording(false);
              setFrequencyData(new Uint8Array(64));
            }

            onTranscript?.(transcript);
            if (data.speech_final && transcriptRef.current) {
              onFinal();
            }
            if (data.is_final && transcript) {
              onChange(transcript);
              onTranscript?.("");
              transcriptRef.current += transcript;
            }
          });

          connection.on(LiveTranscriptionEvents.Metadata, () => {
            console.log("metadata");
          });

          connection.on(LiveTranscriptionEvents.Error, (err) => {
            console.log(err);
          });

          mediaRecorder.ondataavailable = (e) => {
            connection.send(e.data);
          };

          interval = setInterval(() => {
            if (mediaRecorder.state === "recording") {
              mediaRecorder.requestData();
            }
          }, REALTIME_MS);
        });
      })
      .catch(() => {
        console.log("Error getting user media.");
        setIsRecording(false);
        setFrequencyData(new Uint8Array(64));
      });

    return () => {
      isMounted = false;
      mediaRecorder?.stop();
      connection?.requestClose();
      stream?.getTracks().forEach((track) => track.stop());
      clearInterval(interval);
      mediaRecorderRef.current = undefined;
      setFrequencyData(new Uint8Array(64));
      // Clean up frequency data related resources
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [
    isRecording,
    onChange,
    onTranscript,
    deepgram,
    setIsRecording,
    initFrequencyData,
    updateFrequencyData,
    language,
    enableOpenaiTranscription,
  ]);

  if (enableOpenaiTranscription && trackRef.current) {
    trackRef.current.enabled = !isRecording && isConnected;
  }

  useEffect(() => {
    if (!enableOpenaiTranscription) return;
    setIsConnected(false);
    let isMounted = true;
    let track: MediaStreamTrack | undefined;

    const pc = new RTCPeerConnection();
    const dc = pc.createDataChannel("oai-events");

    function handleMessage(e: MessageEvent) {
      const data = JSON.parse(e.data);
      switch (data.type) {
        case "transcription_session.created":
          break;
        case "input_audio_buffer.speech_started":
          setIsRecording(true);
          isSpeechFinal.current = false;
          break;
        case "input_audio_buffer.speech_stopped":
          isSpeechFinal.current = true;
          break;
        case "conversation.item.input_audio_transcription.completed":
          onChange?.(data.transcript);
          transcriptRef.current += data.transcript;
          if (isSpeechFinal.current && transcriptRef.current) {
            transcriptRef.current = "";
            setIsRecording(false);
            setFrequencyData(new Uint8Array(64));
          }
          break;
      }
    }
    dc.addEventListener("message", handleMessage);

    getOpenaiTranscriptionSession({ language, clientDomain }).then(
      async (data) => {
        if (!isMounted) return;
        const EPHEMERAL_KEY = data.client_secret.value;
        const ms = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        initFrequencyData(ms);
        updateFrequencyData();
        track = ms.getAudioTracks()[0];
        trackRef.current = track;
        if (track) {
          pc.addTrack(track);
        }
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const realtimeResponse = await fetch(
          "https://api.openai.com/v1/realtime",
          {
            method: "POST",
            body: offer.sdp,
            headers: {
              Authorization: `Bearer ${EPHEMERAL_KEY}`,
              "Content-Type": "application/sdp",
            },
          },
        );
        await pc.setRemoteDescription({
          type: "answer",
          sdp: await realtimeResponse.text(),
        });
        setIsConnected(true);
      },
    );

    return () => {
      isMounted = false;
      pc.close();
      track?.stop();
      dc.removeEventListener("message", handleMessage);
      setFrequencyData(new Uint8Array(64));
      // Clean up frequency data related resources
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [
    enableOpenaiTranscription,
    onChange,
    setIsRecording,
    initFrequencyData,
    updateFrequencyData,
    language,
    enableOpenaiTranscription,
  ]);

  if (enableOpenaiTranscription && trackRef.current) {
    trackRef.current.enabled = !isVoiceBotSpeaking && isConnected;
  }

  useEffect(() => {
    if (!enableOpenaiTranscription) return;
    setIsConnected(false);
    let isMounted = true;
    let track: MediaStreamTrack | undefined;

    const pc = new RTCPeerConnection();
    const dc = pc.createDataChannel("oai-events");

    function handleMessage(e: MessageEvent) {
      const data = JSON.parse(e.data);
      switch (data.type) {
        case "transcription_session.created":
          break;
        case "input_audio_buffer.speech_started":
          setIsRecording(true);
          isSpeechFinal.current = false;
          break;
        case "input_audio_buffer.speech_stopped":
          isSpeechFinal.current = true;
          break;
        case "conversation.item.input_audio_transcription.completed":
          onChange?.(data.transcript);
          if (isSpeechFinal.current) {
            setIsRecording(false);
            setFrequencyData(new Uint8Array(64));
          }
          break;
      }
    }
    dc.addEventListener("message", handleMessage);

    getOpenaiTranscriptionSession({ language, clientDomain }).then(
      async (data) => {
        if (!isMounted) return;
        const EPHEMERAL_KEY = data.client_secret.value;
        const ms = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        initFrequencyData(ms);
        updateFrequencyData();
        track = ms.getAudioTracks()[0];
        trackRef.current = track;
        if (track) {
          pc.addTrack(track);
        }
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const realtimeResponse = await fetch(
          "https://api.openai.com/v1/realtime",
          {
            method: "POST",
            body: offer.sdp,
            headers: {
              Authorization: `Bearer ${EPHEMERAL_KEY}`,
              "Content-Type": "application/sdp",
            },
          },
        );
        await pc.setRemoteDescription({
          type: "answer",
          sdp: await realtimeResponse.text(),
        });
        setIsConnected(true);
      },
    );

    return () => {
      isMounted = false;
      pc.close();
      track?.stop();
      dc.removeEventListener("message", handleMessage);
      setFrequencyData(new Uint8Array(64));
      // Clean up frequency data related resources
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return {
    isRecording,
    setIsRecording,
    isSpeechFinal,
    mediaRecorder: mediaRecorderRef.current,
    transcriptRef,
    frequencyData, // New return value
    isConnected,
  };
}
